import { openSplitViewState } from '@approvalmax/ui/src/components';
import { mathService } from '@approvalmax/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEvent, useMount, usePrevious, useUnmount, useWindowSize } from 'react-use';

import { defaultSplitViewOpenState, defaultSplitViewWidthState } from './SplitViewWrapper.states';

const { commonConstants } = constants;

export const useDefaultSplitViewOpen = (request: domain.Request) => {
    const { width } = useWindowSize();
    const defaultSplitViewOpen = useAtomValue(defaultSplitViewOpenState);
    const setOpenSplitView = useSetAtom(openSplitViewState);

    useMount(() => {
        if (request.integrationCode && commonConstants.ocrAvailableIntegrationCodes.includes(request.integrationCode)) {
            setOpenSplitView(
                defaultSplitViewOpen !== null
                    ? defaultSplitViewOpen
                    : width >= commonConstants.requestMinLeftPanelWidth + commonConstants.requestMinRightPanelWidth
            );
        }
    });
};

export const useObserveSplitViewItemWidth = () => {
    const { width } = useWindowSize();
    const [splitViewItemWidth, setSplitViewItemWidth] = useState(0);
    const splitViewItemRef = useRef(null);
    const prevWidth = usePrevious(splitViewItemWidth);

    const [defaultSplitViewWidth, setDefaultSplitViewWidth] = useAtom(defaultSplitViewWidthState);

    useMount(() => {
        if (
            width >= commonConstants.requestMinLeftPanelWidth + commonConstants.requestMinRightPanelWidth &&
            !defaultSplitViewWidth
        ) {
            setDefaultSplitViewWidth(
                Math.max(mathService.multiply(width, 0.35), commonConstants.requestMinLeftPanelWidth)
            );
        }
    });

    useEffect(() => {
        if (!splitViewItemRef.current) return;

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setSplitViewItemWidth(entry.contentRect.width);
            }
        });

        observer.observe(splitViewItemRef.current);

        return () => {
            observer.disconnect();
        };
    }, []);
    useEffect(() => {
        // Save the splitView width if it was closed and the current width became 0
        if (splitViewItemWidth === 0 && prevWidth) {
            setDefaultSplitViewWidth(prevWidth);
        }
    }, [splitViewItemWidth, setDefaultSplitViewWidth, prevWidth]);
    useUnmount(() => {
        if (splitViewItemWidth !== 0) {
            setDefaultSplitViewWidth(splitViewItemWidth);
        }
    });

    const beforeUnloadHandler = useCallback(() => {
        if (splitViewItemWidth !== 0) {
            setDefaultSplitViewWidth(splitViewItemWidth);
        }
    }, [setDefaultSplitViewWidth, splitViewItemWidth]);

    useEvent('beforeunload', beforeUnloadHandler);

    return { splitViewItemRef, splitViewItemWidth };
};
